import { useRef, useState } from 'react';
import { formatMinutesToHours } from '../../utils/formatMinutesToHours';
import { t } from '../../i18n/i18n';
import './TestGroupsListItem.scss';
import useCreateScreening from '../../hooks/useCreateScreening';
import { QuestionEnableModal } from './QuestionEnableModal';
import { mapOptionsToLocations, questionsAmount } from '../../utils/tests';
import useTranslations from '../../hooks/useTranslations';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { HiDotsVertical } from 'react-icons/hi';
import { CheckOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { normalizedDescription } from '../../utils/stringUtils.js';
import GenericToggle from '../GenericToggle/GenericToggle.js';
import useClickOutside from '../../hooks/useClickOutside.js';
import MoveTestgroup from '../../assets/screening/MoveTestgroup.jsx';
import DeleteForever from '../../assets/screening/DeleteForever.js';
import { AnswerTypeEnum, TestGroupTypes } from '../../constants/enums.js';
import Ai from '../../assets/additionalQuestions/ai';
import { isAiInterviewStrategyTestGroup } from '../../utils/testgroups';
import DOMPurify from 'dompurify';
import { parseLineHTML } from '../../utils/parseLineHTML';
import AddressSearch from '../Map/MapContainer.jsx';
import OptionMultipleComponent from '../ModalSubmission/OptionMultipleComponent/OptionMultipleComponent.jsx';
import { getPresetStyle } from '../../utils/presetColors';
import { getTranslatedValue } from '../../utils/translationHelpers';

const TestGroupsListItem = ({
  testGroup,
  disableOrderUp,
  disableOrderDown,
  disableQuestionIcon = false,
  disableCustomWeight,
  hideOrderArrows,
  hideDeleteIcon,
  handleOrder,
  handleDelete,
  handleQuestionButton,
  handlePresetSelect,
  selectedPresetId,
  handleOpenPresetModal,
  index,
  className,
  fromCreateEvaluation,
}) => {
  const { key } = useTranslations();
  const { modifyTestWeight } = useCreateScreening();
  const [flipped, setFlipped] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const menuRef = useRef(null);
  const iconRef = useRef(null);
  useClickOutside(menuRef, () => setShowMenu(false), [iconRef]);

  const handleActiveSkipQuestion = ({ hasSkipQuestion }) => {
    handleQuestionButton({ newItem: { ...testGroup, hasSkipQuestion } });
    setShowModal(false);
  };

  const renderPrefilterDetail = (item) => {
    if (!flipped || item?.test?.type !== TestGroupTypes.PRE_FILTER) return null;
    switch (item?.test?.answerType) {
      case AnswerTypeEnum.RADIO:
      case AnswerTypeEnum.CHECK:
      case AnswerTypeEnum.SELECT:
        const testAsSubmission = {
          ...item?.test,
          submittedOptions: item?.test?.acceptedOptions?.find((option) => option.weight === 1)
            ?.options,
          submittedSkip: false,
          acceptedOptions: item?.test?.acceptedOptions,
          options: item?.test?.options,
          answerType: item?.test?.answerType,
          type: item?.test?.type,
        };
        return (
          <OptionMultipleComponent
            submission={testAsSubmission}
            showResult
            isTestgroupListDetail={false}
          />
        );
      case AnswerTypeEnum.TEXT:
        return (
          <span
            className=" font-weight-500 text-xs mb-2 text-color-collapsed-modal"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(
                parseLineHTML(
                  `${
                    t(`EXPECTED_RESPONSE_PRE_FILTER_${item?.test?.writeResponse?.toUpperCase()}`) ??
                    ''
                  }${item?.test?.acceptedOptions?.[0].options?.[0] ?? ''}`
                )
              ),
            }}
          />
        );
        return (
          <span
            className=" font-weight-500 text-xs mb-2 text-color-collapsed-modal"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(
                parseLineHTML(
                  `${
                    t(`EXPECTED_RESPONSE_PRE_FILTER_${item?.test?.writeResponse?.toUpperCase()}`) ??
                    ''
                  }${item?.test?.acceptedOptions?.[0].options?.[0] ?? ''}`
                )
              ),
            }}
          />
        );
      case AnswerTypeEnum.LOCATIONS:
        const acceptedMarkers = mapOptionsToLocations(
          item?.test?.options,
          item?.test?.acceptedOptions
        );
        const markers = [...acceptedMarkers];
        if (markers) {
          return (
            <div className="p-4">
              <AddressSearch limitMarkers={0} markers={markers} />
            </div>
          );
        }
        return null;
      default:
        return null;
    }
  };

  return (
    <div className={`row d-flex flex-column w-100 gap-1 ${className}`}>
      <div className="col-1">
        <span className="tg-number-text">{index >= 0 ? index + 1 : ' '}</span>
      </div>
      <div className="col-11">
        <div className={`row testgroup-listitem-container list-item-card ${flipped && 'flipped'}`}>
          <div className="list-item-card-front">
            <div className="col-9 d-flex flex-column flex-md-row pt-1">
              <div className="d-flex flex-column w-100 h-100 p-1">
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <span className="text-xs font-weight-600">
                    {testGroup?.type === TestGroupTypes.PRE_FILTER
                      ? t('TESTGROUP_PRE_FILTER_TITLE_NAME')
                      : testGroup?.nameTranslated?.[key] || testGroup.name}
                  </span>
                </div>
                <div className="d-flex flex-grow-1 gap-1">
                  <div className="d-flex flex-column w-50 gap-1">
                    <div className="d-flex align-items-center">
                      {testGroup?.type === TestGroupTypes.CHALLENGE_ASYNC_AI ? (
                        <span className="text-xs font-weight-400">
                          {t(
                            testGroup?.tests?.[0]?.promptSubType ??
                              `${testGroup?.tests?.[0]?.test?.promptSubType}_SHORT`
                          )}
                        </span>
                      ) : (
                        <>
                          <CheckOutlined className="mr-1 testformat-icon" />
                          <span className="text-xs font-weight-400">
                            {`${questionsAmount(testGroup)} ${t(
                              'CREATE_TEST_GROUP_QUESTIONS'
                            ).toLowerCase()}`}
                          </span>
                        </>
                      )}
                    </div>
                    <div className="d-flex align-items-center">
                      <ClockCircleOutlined className="mr-1 testformat-icon" />
                      <span className="text-xs font-weight-400">
                        {testGroup?.type === TestGroupTypes.PRE_FILTER
                          ? t('NO_TIME_RESTRICTION')
                          : testGroup?.type === TestGroupTypes.CHALLENGE_ASYNC_AI
                          ? t('ASYNC_RANGE_DEFINED')
                          : formatMinutesToHours(testGroup?.duration ?? 0)}
                      </span>
                    </div>
                    {isAiInterviewStrategyTestGroup(testGroup) ? (
                      <div className="d-flex align-items-center">
                        <div className="mr-1">
                          <Ai color="#bcbcbc" />
                        </div>
                        <span className="text-xs font-weight-400">
                          {t('TESTGROUP_STRATEGY_AI_INTERVIEW_LABEL')}
                        </span>
                      </div>
                    ) : null}
                    <div className="d-flex flex-wrap gap-1">
                      {testGroup?.testFormats?.map((format, index) => (
                        <OverlayTrigger
                          key={`test-format-list-item-${index}`}
                          placement="top"
                          overlay={<Tooltip id="tooltip-top">{format.name}</Tooltip>}
                        >
                          <img
                            src={format?.iconUrl}
                            alt={format.name}
                            className="testformat-icon"
                          />
                        </OverlayTrigger>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-3 bg-grey-3 d-flex flex-column border-grey-rounded pt-2 position-relative">
              <div ref={iconRef} className="d-flex w-100 justify-content-end position-relative">
                <HiDotsVertical
                  className="p-0 m-0 cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMenu((prev) => !prev);
                  }}
                />
                <div
                  className={`position-absolute ${
                    showMenu && 'show-menu'
                  } testgroup-list-item-menu`}
                  ref={menuRef}
                >
                  {!hideOrderArrows && (
                    <>
                      <button
                        disabled={disableOrderUp}
                        className="w-100 bg-transparent border-0 outline-none d-flex justify-content-between align-items-center gap-1"
                        type="button"
                        onClick={() =>
                          handleOrder(testGroup.order, testGroup.order - 1, 'allTests')
                        }
                        data-testid="tg-list-item-disabled-order-up-btn"
                      >
                        <span>{t('TESTLIST_ITEM_UP_POSITION')}</span>
                        <MoveTestgroup />
                      </button>
                      <button
                        disabled={disableOrderDown}
                        className="w-100 bg-transparent border-0 outline-none d-flex justify-content-between align-items-center  gap-1"
                        type="button"
                        onClick={() =>
                          handleOrder(testGroup.order, testGroup.order + 1, 'allTests')
                        }
                        data-testid="tg-list-item-disabled-order-down-btn"
                      >
                        <span>{t('TESTLIST_ITEM_DOWN_POSITION')}</span>
                        <MoveTestgroup rotate />
                      </button>
                    </>
                  )}
                  <div
                    onClick={() => !disableQuestionIcon && setShowModal(true)}
                    className="d-flex w-100 justify-content-between align-items-center  gap-1"
                  >
                    <span>{t('LISTITEM_MENU_SKIP_QUESTIONS')}</span>
                    <GenericToggle
                      data-testid="toggle"
                      defaultActive={testGroup?.hasSkipQuestion ?? true}
                      value={testGroup?.hasSkipQuestion ?? true}
                      actionFunction={() => !disableQuestionIcon && setShowModal(true)}
                    />
                  </div>
                  {!hideDeleteIcon && (
                    <button
                      onClick={handleDelete}
                      type="button"
                      className="bg-transparent border-0 outline-none w-100 d-flex justify-content-between align-items-center  gap-1"
                      data-testid="tg-list-item-delete-btn"
                    >
                      <span>{t('LISTITEM_MENU_SKIP_DELETE')}</span>
                      <DeleteForever />
                    </button>
                  )}
                </div>
              </div>
              {[TestGroupTypes.QUALITATIVE, TestGroupTypes.PRE_FILTER].includes(testGroup?.type) ||
              (disableCustomWeight && !testGroup?.weight) ? (
                <div className="d-flex justify-content-center">
                  <span className="font-italic text-xs font-weight-500 mt-4 word-wrap">
                    {t(`CREATE_SCREENING_${testGroup.type.toUpperCase()}_LABEL`)}
                  </span>
                </div>
              ) : (
                <div
                  data-testid="tg-list-item-weight-input"
                  className={`d-flex bottom-border justify-content-center align-items-center mt-4
                  ${(testGroup?.weight === '0' || !testGroup.weight) && 'wrong-weight'}`}
                >
                  <input
                    disabled={disableCustomWeight}
                    className="outline-none bg-transparent border-0 text-center w-100"
                    min="0"
                    max="100"
                    value={testGroup?.weight ?? ''}
                    onKeyDown={(e) => {
                      const excludedKeys = ['-', '+', 'e'];
                      if (excludedKeys.includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    onChange={(e) => modifyTestWeight(testGroup?.id, e.target.value)}
                    type="number"
                    data-testid="tg-list-item-custom-weight-input"
                  />
                  <span className=" " data-testid="tg-list-item-percent-span">
                    %
                  </span>
                </div>
              )}

              {testGroup?.selectedPresetId !== undefined &&
                testGroup?.advancedSetting?.available &&
                testGroup?.advancedSetting?.preSettings?.length > 0 &&
                (() => {
                  const selectedPreset = testGroup.advancedSetting.preSettings.find(
                    (preset) => String(preset.id) === String(testGroup.selectedPresetId)
                  );
                  const { backgroundColor, color, border } = (() => {
                    const style = getPresetStyle(testGroup, testGroup.selectedPresetId);
                    return {
                      backgroundColor: style?.backgroundColor || '#e8f4f8',
                      boder: 'none',
                    };
                  })();

                  return (
                    <div
                      className="preset-badge"
                      style={{
                        backgroundColor,
                        color,
                        border,
                        cursor: 'pointer',
                      }}
                      onClick={() => handleOpenPresetModal && handleOpenPresetModal(testGroup)}
                    >
                      {getTranslatedValue(
                        selectedPreset?.displayName ??
                          selectedPreset?.name ??
                          `Config ${testGroup.selectedPresetId}`,
                        key
                      )}
                    </div>
                  );
                })()}
            </div>
          </div>
          <div
            className="list-item-card-back d-flex flex-column py-2 px-3"
            onClick={() => setFlipped(false)}
          >
            <span>{t('TESTGROUP_OBJECTIVES_OF_THE_EVALUATION')}</span>
            <div className="d-flex flex-wrap test-objectives-content">
              {testGroup.objetives?.[key]?.length || testGroup.objetives?.es?.length
                ? (testGroup.objetives?.[key] || testGroup.objetives?.es).map((item, index) => (
                    <OverlayTrigger
                      key={`${testGroup?.id}-obj-${index}`}
                      placement="top"
                      overlay={<Tooltip id="tooltip-top">{item}</Tooltip>}
                    >
                      <p className="objective-pill texts">{normalizedDescription(item, 16)}</p>
                    </OverlayTrigger>
                  ))
                : t('MODAL_SUBMISSION_DEFAULT_OBJECTIVES')
                    ?.split(',')
                    ?.map((item, index) => (
                      <OverlayTrigger
                        key={`${testGroup?.id}-obj-${index}`}
                        placement="top"
                        overlay={<Tooltip id="tooltip-top">{item}</Tooltip>}
                      >
                        <p className="objective-pill texts">{normalizedDescription(item, 16)}</p>
                      </OverlayTrigger>
                    ))}
            </div>
            <span>{t('TESTGROUP_TARGET_ROLES')}</span>
            <div className="test-roles-content">
              {testGroup.roles?.length
                ? testGroup.roles?.slice(0, 5).map((role, index) => (
                    <OverlayTrigger
                      key={`${testGroup?.id}-role-${index}`}
                      placement="top"
                      overlay={<Tooltip id="tooltip-top">{t(role?.whitelabelName)}</Tooltip>}
                    >
                      <p className="role-pill texts">
                        {normalizedDescription(t(role?.whitelabelName), 16)}
                      </p>
                    </OverlayTrigger>
                  ))
                : t('MODAL_SUBMISSION_DEFAULT_TARGET_ROLES')
                    ?.split(',')
                    ?.map((item, index) => (
                      <p key={`${testGroup?.id}-role-${index}`} className="role-pill texts">
                        {normalizedDescription(item, 16)}
                      </p>
                    ))}
            </div>
          </div>
          {testGroup?.tests && disableQuestionIcon && (
            <div
              className={`d-flex flex-column w-full  mt-1 mb-1 mx-2 ${
                flipped ? 'flip-element' : ''
              }`}
            >
              <div className="d-flex flex-column w-full  my-1 bg-white">
                <span className="d-block  font-weight-800 text-xs text-color-item-test-group text-truncate media-body my-2">
                  {t('QUESTIONS')}
                </span>
                {testGroup?.tests?.map((item) => (
                  <div
                    key={item.testId}
                    className="d-flex flex-column mb-2 p-3 border-item-test-group"
                    aria-hidden="true"
                  >
                    <div className="justify-content-between d-flex flex-row justify-content-between align-items-center position-relative">
                      <span className="d-block  font-weight-600 text-xs text-color-item-test-group text-truncate media-body">
                        {testGroup?.type === TestGroupTypes.CHALLENGE_ASYNC_AI
                          ? item.test?.files?.[0]?.displayName
                          : item?.test?.text}
                      </span>
                      <span className="ticket-technology-frontend ticket-padding font-weight-bold  text-uppercase px-3 mr-auto mr-md-0 mt-2 mt-md-0 ml-2">
                        {item?.test?.type === TestGroupTypes.PRE_FILTER
                          ? t(`CREATE_PREFILTER_QUESTIONS_${item.test?.answerType}`)
                          : testGroup?.type === TestGroupTypes.CHALLENGE_ASYNC_AI
                          ? t(`CREATE_CHALLENGE_QUESTIONS_${item.test?.answerType}`)
                          : t(`CREATE_SCREENING_YOUR_QUESTIONS_${item.test?.answerType}`)}
                      </span>
                    </div>
                    {renderPrefilterDetail(item ?? null)}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      {!disableQuestionIcon && testGroup?.type !== TestGroupTypes.PRE_FILTER && (
        <QuestionEnableModal
          data={{ x: '' }}
          setShowModal={setShowModal}
          showModal={showModal}
          submitAction={() => {
            handleActiveSkipQuestion({
              hasSkipQuestion:
                testGroup?.hasSkipQuestion === undefined ? false : !testGroup?.hasSkipQuestion,
            });
          }}
          isQuestionEnabled={testGroup?.hasSkipQuestion ?? true}
        />
      )}
    </div>
  );
};

export default TestGroupsListItem;
